import i18n from "@/i18n.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { SelectItem } from "@skailer-com/uikit";
import { ApiWorkflowShort } from "@/types/workflows/workflow.types.ts";
import { ApiCustomFieldValue, ApiFieldShort, ApiTableShort } from "@/types/custom-field.types.ts";
import { ApiFormResultCompensationLine } from "@/types/forms/form.types.ts";
import { ApiFilterBase } from "@skailer-com/uikit/core";

export enum ApiApprovalRequestStatus {
  awaiting = "awaiting",
  approved = "approved",
  rejected = "rejected",
  canceled = "canceled",
}

export const getRequestStatusTitle = (status: ApiApprovalRequestStatus) => {
  const statusNames: Record<ApiApprovalRequestStatus, string> = {
    [ApiApprovalRequestStatus.approved]: i18n.t("approval:request_status_approved"),
    [ApiApprovalRequestStatus.awaiting]: i18n.t("approval:request_status_awaiting"),
    [ApiApprovalRequestStatus.rejected]: i18n.t("approval:request_status_rejected"),
    [ApiApprovalRequestStatus.canceled]: i18n.t("approval:request_status_canceled"),
  };

  return statusNames[status];
};

export const getApprovalStatusSelectItems = (): SelectItem[] => {
  return [
    ApiApprovalRequestStatus.approved,
    ApiApprovalRequestStatus.awaiting,
    ApiApprovalRequestStatus.rejected,
    ApiApprovalRequestStatus.canceled,
  ].map((status) => ({
    id: status,
    title: getRequestStatusTitle(status),
  }));
};

export enum ApiApprovalRequestConfirmationStatus {
  queued = "queued",
  awaiting = "awaiting",
  approved = "approved",
  rejected = "rejected",
  skipped = "skipped",
}

export interface ApiApprovalRequestConfirmationACL {
  can_edit_comment: boolean;
}

export interface ApiApprovalRequestConfirmation {
  from_employee: ApiEmployee;
  confirmed_at?: Date;
  rejected_at?: Date;
  is_bypass_approval_flow: boolean;
  status: ApiApprovalRequestConfirmationStatus;
  comment: string | null;
  acl: ApiApprovalRequestConfirmationACL;
}

export interface ApiAccessControlList {
  can_view: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_cancel?: boolean;
  can_reject?: boolean;
  can_approve?: boolean;
  can_bypass_flow?: boolean;

  can_connect_job?: boolean;
}

export enum ApiApprovalRequestType {
  hiring_plan = "hiring_plan",
  offer = "offer",
  time_off = "time_off",
  workflow = "workflow",
}

export interface ApiApprovalRequestDetails {
  id: number;
  datetime_created_at: Date;
  employee: ApiEmployee;
  type: ApiApprovalRequestType;
  status: ApiApprovalRequestStatus;
  confirmations?: ApiApprovalRequestConfirmation[];
  created_by?: ApiEmployee;
}

export interface ApiApprovalRequest {
  approval_request_details: ApiApprovalRequestDetails | null;
  acl: ApiAccessControlList;
}

export interface ApiApprovalRequestWorkflowData {
  workflow: ApiWorkflowShort;
  title: string;
  changes: ApiSurveyWriteToFieldChange[];
}

export interface ApiSurveyWriteToFieldChange {
  field: ApiFieldShort;
  table: ApiTableShort | null;
  value_before: ApiCustomFieldValue | null;
  value_after: ApiCustomFieldValue | null;
  compensation_lines_before: ApiFormResultCompensationLine[] | null;
  compensation_lines_after: ApiFormResultCompensationLine[] | null;
  datetime_created_at: Date;
}

export interface ApiApprovalRequestFull extends ApiApprovalRequest {
  workflow_data: ApiApprovalRequestWorkflowData | null;
}

export interface ApiMyWorkflowApprovalRequest extends Omit<
  ApiApprovalRequestFull,
  "approval_request_details" | "workflow_data"
> {
  approval_request_details: ApiApprovalRequestDetails;
  workflow_data: ApiApprovalRequestWorkflowData;
}

export interface ApiApprovalRequestApprovePayload {
  is_bypass_approval_flow?: boolean;
  comment?: string | null;
}

export interface ApiApprovalRequestConfirmationCommentUpdate {
  comment: string | null;
}

export interface ApiApprovalRequestMyFilter extends ApiFilterBase {
  statuses?: ApiApprovalRequestStatus[];
}
